import { faCloudArrowUp, faSpinner, faX } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FileUpload } from 'primereact/fileupload'
import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react'
import classes from './ModelUploadColumn.module.scss'
import {
  MODEL_ALLOWED_FILE_TYPES,
  MODEL_INVALID_IMAGE_TYPES,
} from '../../../../../utils/constants/model-section.constants'
import './ModelUploadColumn.css'
import { Tooltip } from 'primereact/tooltip'
import useToastr from '../../../../../hooks/useToastr'
import { stripBase64ImagePrefix } from '../../../../../utils/helpers/base64Utils'
import {
  removeAssetDocument,
  updateAssetDocuments,
} from '../../../../../features/createCertificate/createCertificateSlice'
import { useDispatch } from 'react-redux'
import CertificateListService from '../../../../../services/CertificateListService'
import { saveAs } from 'file-saver'
import React from 'react'
import { createPortal } from 'react-dom'

interface Props {
  assetId: any
  column: string
  isBatchEdit?: boolean
  hasRowEditMode?: boolean
  isForBatchEdit?: boolean
  onAddColumnFile: (file: any) => void
  onUpdateColumnFile: (assetId: string, objectURL: string, column: string) => void
  columnFiles: any[]
  onDeleteColumnFile: (assetId: string, column: string) => void
  modelName: string
}

const ModelUploadColumn = React.memo(
  ({
    assetId,
    column,
    isBatchEdit,
    hasRowEditMode,
    isForBatchEdit,
    onAddColumnFile,
    onUpdateColumnFile,
    columnFiles,
    onDeleteColumnFile,
    modelName,
  }: Props) => {
    const dispatch = useDispatch()

    const ref = useRef<any>(null)
    const uploadRef = useRef<any>(null)

    const [file, setFile] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [hovering, setHovering] = useState<boolean>(false)
    const [previewStyle, setPreviewStyle] = useState<CSSProperties | null>(null)
    const [preview, setPreview] = useState<{
      visible: boolean
      anchorRect: DOMRect | null
    } | null>({
      visible: false,
      anchorRect: null,
    })

    const previewRef = useRef<HTMLDivElement>(null)

    const { showError } = useToastr()

    const id = `${column}_${assetId}`

    const colFile = columnFiles?.find((f: any) => f.assetId === assetId && f.column === column)

    useEffect(() => {
      const fetchDocument = async () => {
        if (colFile && !colFile?.isDeleted) return
        setLoading(true)
        try {
          const payload = {
            assetId,
            modelName,
          }
          const result = await CertificateListService.assetDocuments(payload)
          let newFile
          if (result?.data?.length > 0) {
            const { data } = result

            const assetDocument = data.find(
              (d: any) => d.assetId === assetId && d.docType === column
            )
            newFile = {
              assetId,
              name: assetDocument.docName,
              objectURL: '',
              docId: assetDocument.docId,
              column,
              isDeleted: false,
            }

            setFile(newFile)
            onAddColumnFile(newFile)
          } else {
            const newFile = {
              assetId,
              name: '',
              objectURL: '',
              docId: '',
              column,
              isDeleted: false,
            }
            onAddColumnFile(newFile)
          }
        } catch (error) {
        } finally {
          setLoading(false)
        }
      }
      fetchDocument()
    }, [])

    useEffect(() => {
      if (colFile && !colFile?.isDeleted) {
        setFile(colFile)
      }
    }, [colFile])

    const objectURLToBase64 = async (objectUrl: string) => {
      try {
        if (objectUrl === '') return objectUrl
        // Fetch the blob from the ObjectURL
        const response = await fetch(objectUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()

        // Convert blob to Base64 using FileReader
        return await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result) // reader.result includes "data:*/*;base64,"
          reader.onerror = () => reject(new Error('Failed to read blob as Base64'))
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error('Error converting ObjectURL to Base64:', error)
        throw error
      }
    }

    useEffect(() => {
      if (file && file.objectURL) {
        ;(async () => {
          const base64String = await objectURLToBase64(file?.objectURL)
          const rawBase64 = stripBase64ImagePrefix(base64String as string)

          dispatch(
            updateAssetDocuments({
              assetId,
              column,
              fileName: file.name,
              rawBase64,
              docId: file.docId ?? '',
            })
          )
        })()
      }
    }, [file?.name])

    useLayoutEffect(() => {
      if (!preview?.anchorRect || !previewRef.current) return

      const node = previewRef.current

      const observer = new ResizeObserver(() => {
        const overlayHeight = node.offsetHeight
        if (!preview?.anchorRect || !previewRef.current) return

        setPreviewStyle({
          position: 'fixed',
          left: preview.anchorRect.left + preview.anchorRect.width / 2,
          top: preview.anchorRect.top - overlayHeight - 8,
          transform: 'translateX(-50%)',
          zIndex: 99999,
        })
      })

      observer.observe(node)

      return () => {
        observer.disconnect()
      }
    }, [preview?.anchorRect])

    const handleRemoveImage = (e: any) => {
      e.stopPropagation()
      setFile(null)

      dispatch(removeAssetDocument({ assetId, column }))
      onDeleteColumnFile(assetId, column)
    }

    const customHeader = (options: any) => {
      const { chooseButton } = options

      const enhancedChooseButton = React.cloneElement(chooseButton, {
        onClick: (e: React.MouseEvent) => {
          chooseButton.props?.onClick?.(e)
          uploadRef.current?.hide()
        },
        onMouseEnter: (e: React.MouseEvent) => {
          uploadRef.current?.show(e)
        },
        onMouseLeave: () => {
          uploadRef.current?.hide()
        },
      })

      return <>{enhancedChooseButton}</>
    }

    const emptyTemplate = () => {
      if (file && file?.name) return <></>

      return (
        <div className={classes['empty-template']}>
          {!loading && (
            <>
              <span>
                <FontAwesomeIcon icon={faCloudArrowUp} />
              </span>
              <span className={classes['drag-drop-span']}>or Drag and Drop file here</span>
            </>
          )}
        </div>
      )
    }

    const handleOnSelect = (e: any) => {
      const fileList: FileList = e.files

      const validFiles = Array.from(fileList).filter(
        file =>
          MODEL_ALLOWED_FILE_TYPES.includes(file.type) &&
          file.size <= 1000000 &&
          !MODEL_INVALID_IMAGE_TYPES.includes(file.name.split('.').pop()?.toLocaleLowerCase()!)
      )
      if (validFiles.length !== e.files.length) {
        showError(
          'Image upload failed',
          'The image is either an unsupported file type or exceeds the size limit. Try a JPEG or PNG image within 1MB file size.'
        )
      } else {
        const selectedFile = validFiles[0]
        setFile(selectedFile)

        const newFile = {
          assetId,
          name: selectedFile.name,
          objectURL: URL.createObjectURL(selectedFile),
          docId: '',
          column,
          isDeleted: false,
        }
        onAddColumnFile(newFile)
      }

      ref.current?.clear()
    }

    const handleHover = async (e: any) => {
      if (isBatchEdit) return

      const rect = e.currentTarget.getBoundingClientRect()
      setPreview({
        visible: true,
        anchorRect: rect,
      })

      try {
        if (file && !file?.objectURL) {
          setLoading(true)

          const payload = {
            docName: file?.docId,
          }

          const res = await CertificateListService.getAssetImage(payload)

          if (res?.data) {
            setFile((prev: any) => ({
              ...prev,
              objectURL: res.data,
            }))

            onUpdateColumnFile(assetId, res.data ?? '', column)
          }
        }
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }

    const handleDownload = async (e: any) => {
      e.stopPropagation()
      if (isBatchEdit) return

      if (file) {
        const base64String = await objectURLToBase64(file?.objectURL)
        const rawBase64 = stripBase64ImagePrefix(base64String as string)

        const payload = {
          fileName: file?.name,
          base64Content: rawBase64,
        }

        const res = await CertificateListService.downloadAssetImage(payload)
        if (res) {
          saveAs(res.data, file?.name)
        }
      }
    }

    const handleMouseEnter = () => {
      setHovering(true)
    }

    const handleMouseLeave = () => {
      setHovering(false)
    }

    const handleRemoveToolTip = () => {
      setPreview(null)
    }

    if (isForBatchEdit) return <></>

    return (
      <div className={`${classes['model-upload-column_container']}  `}>
        <div
          className={`${classes['name-upload-container']} remove-file-message `}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            opacity: loading ? 0.5 : 1,
            pointerEvents: isBatchEdit || hasRowEditMode ? 'none' : 'auto',
          }}
        >
          <div style={{ position: 'relative' }}>
            <FileUpload
              ref={ref}
              mode="advanced"
              headerTemplate={customHeader}
              emptyTemplate={emptyTemplate}
              progressBarTemplate={() => <></>}
              chooseOptions={{
                label: 'Upload',
                icon: '',
                className: `upload-button_${assetId}_${column} ${classes['choose-button-containter']} ${file?.name ? classes['hidden'] : ''} ${loading ? classes['loading'] : ''}`,
              }}
              contentClassName={`${classes['drag-drop-content']} ${file?.name ? classes['hide-border'] : ''} ${loading ? classes['loading'] : ''}`}
              className={`${classes['file-upload-container']} ${file?.name ? classes['resize'] : ''} ${isBatchEdit || hasRowEditMode ? classes['edit-mode'] : ''} `}
              maxFileSize={1000000} //1mb
              multiple={true}
              accept={MODEL_ALLOWED_FILE_TYPES.join(',')}
              onSelect={handleOnSelect}
            />
            {!file?.name && loading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  left: '8rem',
                  marginLeft: '-14.5rem',
                }}
              >
                <FontAwesomeIcon icon={faSpinner} spin={true} />
              </div>
            )}
          </div>
          <Tooltip
            ref={uploadRef}
            target={`.upload-button_${assetId}_${column}`}
            content="Click and search an image to upload"
            position="right"
            showEvent=""
            hideEvent=""
          />
          {file?.name && (
            <>
              <span className={classes['file-item-container']}>
                <span
                  className={`${classes['file-name']}  model_${id}
                                    ${hovering ? classes['hover'] : ''}
                                    ${isBatchEdit || hasRowEditMode ? classes['edit-mode'] : ''}
                                    `}
                  onMouseEnter={handleHover}
                  onClick={handleDownload}
                  onMouseLeave={handleRemoveToolTip}
                >
                  {file.name}
                </span>
              </span>

              <Tooltip
                target={`#removeImage`}
                position="bottom"
                content="Remove image"
                className="model-close-icon"
              />
            </>
          )}
        </div>
        {file?.name && (
          <>
            <FontAwesomeIcon
              id={`removeImage_${id}`}
              icon={faX}
              className={`${classes['file-remove']}  
                                ${isBatchEdit || hasRowEditMode || loading ? classes['edit-mode'] : ''} `}
              onClick={handleRemoveImage}
            />
            <Tooltip
              target={`#removeImage_${id}`}
              position="bottom"
              content="Remove image"
              className="model-close-icon"
            />
          </>
        )}

        {preview?.visible &&
          createPortal(
            <div
              ref={previewRef}
              style={{
                ...(previewStyle ?? { visibility: 'hidden' }),
                pointerEvents: 'none',
              }}
              className={classes['tooltip-preview']}
            >
              <div className={classes['tooltip-content']}>
                {file?.objectURL ? (
                  <img
                    src={file.objectURL}
                    style={{ maxWidth: 350, maxHeight: 350 }}
                    alt={file.name}
                  />
                ) : (
                  <div className={classes['tooltip-loading']}>
                    <div
                      style={{
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FontAwesomeIcon icon={faSpinner} spin={true} />
                    </div>
                  </div>
                )}
                <div className={classes['tooltip-filename']}>{file.name}</div>
              </div>
            </div>,
            document.body
          )}
      </div>
    )
  }
)

export default ModelUploadColumn
